import React from 'react';

import { batch } from 'react-redux';

import FormHeader from 'prequalificationV2/components/FormHeader';
import { PreQualificationStep, setPreQualificationStep } from 'prequalificationV2/actions';
import { setApplicationType } from 'prequalificationV2/actions/application';
import { clearCoBorrower } from 'prequalificationV2/actions/coBorrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import { PhoneIcon } from 'components/Icons';
import { AlreadyHaveActiveApplicationType } from 'enums/AlreadyHaveActiveApplicationType';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { ButtonType } from 'enums/ButtonType';

import styles from './AlreadyHaveActiveApplication.module.scss';

const BORROWER_TITLE_TEXT = `It looks like you already have an active application.\n`;
const COBORROWER_TITLE_TEXT = 'is already on an active loan inquiry in our system.';
const PROCEED_INDIVIDUALLY = 'Continue Without Coborrower';
const ADD_NEW_COBORROWER = 'Add a New Coborrower';

interface AlreadyHaveActiveApplicationProps {
  pageType: AlreadyHaveActiveApplicationType;
}

const AlreadyHaveActiveApplication = ({ pageType }: AlreadyHaveActiveApplicationProps) => {
  const navigator = useAppNavigate();
  const dispatch = useAppDispatch();
  const { isIframe } = useAppSelector((state) => state.data);
  const { [LoanInformationVariables.AssignedConsultant]: consultant } = useAppSelector((state) => state.application);
  const { firstName: coBorrowerFirstName, lastName: coBorrowerLastName } = useAppSelector((state) => state.coBorrower);
  const { nextPreQualificationStep } = useAppSelector((state) => state.stages);

  const renderTitle = () => {
    const handleRedirect = () => {
      if (nextPreQualificationStep) {
        dispatch(setPreQualificationStep(nextPreQualificationStep));
        navigator(AppRoutes.Prequalification);
      } else {
        navigator(AppRoutes.Dashboard);
      }
    };

    return (
      <>
        {pageType === AlreadyHaveActiveApplicationType.CoBorrower ? (
          <div className={styles.whiteSpacesPreWrap}>
            {coBorrowerFirstName} {coBorrowerLastName} {COBORROWER_TITLE_TEXT}
          </div>
        ) : (
          <>
            <div className={styles.topRow}>{BORROWER_TITLE_TEXT}</div>
            {pageType === AlreadyHaveActiveApplicationType.Borrower && !isIframe && (
              <div>
                <span className={styles.redirectLink} onClick={handleRedirect}>
                  Click here
                </span>{' '}
                to continue.
              </div>
            )}
          </>
        )}
      </>
    );
  };
  const handleAddNewCoborrower = () => {
    batch(() => {
      dispatch(clearCoBorrower());
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    });
  };
  const handleProceedIndividually = () => {
    batch(() => {
      dispatch(clearCoBorrower());
      dispatch(setApplicationType(ApplicationType.Individual));
      dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
    });
  };

  return (
    <>
      <FormHeader title={renderTitle()} />
      {pageType === AlreadyHaveActiveApplicationType.Borrower && !!isIframe && (
        <FormBody>
          <p className={styles.onceYourCredit}>Please call your HFS loan consultant {consultant?.name}</p>
          {
            <div className={styles.infoContainer}>
              <div>
                <PhoneIcon className={styles.image} />
                <a className={styles.phoneLink} href={`tel:${consultant?.phone}`}>
                  {consultant?.phone}
                </a>
              </div>
            </div>
          }
        </FormBody>
      )}
      {pageType === AlreadyHaveActiveApplicationType.CoBorrower && (
        <div className={styles.coborrowerText}>
          It seems that your coborrower {coBorrowerFirstName} {coBorrowerLastName} is already listed on an active loan
          inquiry in our system and is not eligible to be a coborrower on this loan inquiry. You may either continue
          without a coborrower or you can add an alternate coborrower.
        </div>
      )}
      {pageType === AlreadyHaveActiveApplicationType.CoBorrower && (
        <div className={styles.coborrowerButtons}>
          <Button
            buttonType={ButtonType.Secondary}
            className={styles.buttonAddNewCoborrower}
            onClick={handleAddNewCoborrower}
            withPlus
          >
            {ADD_NEW_COBORROWER}
          </Button>
          <Button className={styles.buttonProceedIndividually} onClick={handleProceedIndividually}>
            {PROCEED_INDIVIDUALLY}
          </Button>
        </div>
      )}
    </>
  );
};

export default AlreadyHaveActiveApplication;
